import React from "react";
import { Circle, Download, Filter } from "lucide-react";
import {
  Button,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components";
import { Col } from "../../../components/col";
import { Row } from "../../../components/row";
import { useEffect, useRef, useState } from "react";
import { DatePicker, DatePickerHandle } from "../../../components/date-picker";
import { MultiSelect } from "../../../components/customs/multi-select";
import { DemographicTypes } from "../data/demographic";
import { endOfDay } from "date-fns/endOfDay";
import { endOfQuarter } from "date-fns/endOfQuarter";
import { startOfDay } from "date-fns/startOfDay";
import { startOfQuarter } from "date-fns/startOfQuarter";
import { subDays } from "date-fns/subDays";
import { subMonths } from "date-fns/subMonths";
import { LIVE_EVENTS_MAP_KEY, USER_DISTRIBUTIONS_MAP_KEY } from "../commons/Constant";
import { ActionModal } from "../../../components/modal/action-modal";
import AlertTimeFilterPopup from "./AlertTimeFilterPopup";
import { EventStatus, type HeatmapFilterModel, type OptionModel } from "../types/Filter";
import { useAuthSession } from "../../auth/shared/hooks/auth-session-context";
import { FilterControlsSkeleton } from "./region/region-skeletons";
import { RegionState } from "./region/RegionState";

interface FiltersProps {
  filters: HeatmapFilterModel | null;
  onFiltersChange: React.Dispatch<React.SetStateAction<HeatmapFilterModel | null>>;
  neighborhoodOptions: OptionModel[];
  categoryOptions: OptionModel[];
  displayMap: string;
  onDisplayMapChange: (mapKey: string) => void;
  onApply: (filters: HeatmapFilterModel | null) => void;
  onExport: () => void;
  isExporting?: boolean;
  isOptionsLoading?: boolean;
  optionsError?: string | null;
  onRetryOptions?: () => void;
}

const Filters: React.FC<FiltersProps> = ({
  filters,
  onFiltersChange: setFilters,
  neighborhoodOptions,
  categoryOptions,
  displayMap,
  onDisplayMapChange: changeDisplayMap,
  onApply,
  onExport,
  isExporting = false,
  isOptionsLoading = false,
  optionsError,
  onRetryOptions,
}) => {
  const { user } = useAuthSession();
  const [showNeighborhoodOptions, setShowNeighborhoodOptions] = useState(false);
  const [showDemographicOptions, setShowDemographicOptions] = useState(false);
  const [showCategoryOptions, setShowCategoryOptions] = useState(false);
  const [alertFilterOpen, setAlertFilterOpen] = useState(false);

  const fromDatePickerRef = useRef<DatePickerHandle | null>(null);
  const toDatePickerRef = useRef<DatePickerHandle | null>(null);
  const [eventStatus, setEventStatus] = useState<EventStatus | null>(null);

  const openAlertFilterPopup = () => setAlertFilterOpen(true);
  const closeAlertFilterPopup = () => setAlertFilterOpen(false);

  const getDemographicTypeOptions = () => {
    return DemographicTypes.map((dt) => ({ value: dt.key.toString(), label: dt.name }));
  };

  const handleChangeEventStatus = (value: string) => {
    const eventStatus = Number(value) as EventStatus;
    setEventStatus(eventStatus);
  };

  const handleChangeQuickFilter = (value: string) => {
    const v = value ? Number(value) : null;
    const today = new Date();
    let fromDate: Date | undefined = undefined;
    let toDate: Date | undefined = undefined;

    //7 days
    if (v === 1) {
      fromDate = startOfDay(subDays(today, 6));
      toDate = endOfDay(today);
    } else if (v === 2) {
      //30 days
      fromDate = startOfDay(subDays(today, 29));
      toDate = endOfDay(today);
    } else if (v === 3) {
      //last quarter
      const prevQuarterRef = subMonths(today, 3);
      fromDate = startOfQuarter(prevQuarterRef);
      toDate = endOfQuarter(prevQuarterRef);
    } else {
      // reset quick filter
      fromDate = undefined;
      toDate = undefined;
    }

    setFilters((f) => ({
      ...(f ?? {}),
      quickFilterDays: v ?? undefined,
      fromDate,
      toDate,
    }));
    changeDisplayMap(LIVE_EVENTS_MAP_KEY);
  };

  const handleAlertOk = () => {
    closeAlertFilterPopup();
    if (!filters?.fromDate) {
      fromDatePickerRef.current?.setOpen(true);
    } else if (!filters?.toDate) {
      toDatePickerRef.current?.setOpen(true);
    }
  };

  const handleClearDateFilter = () => {
    closeAlertFilterPopup();
    setFilters((filters) => ({
      ...(filters ?? {}),
      fromDate: undefined,
      toDate: undefined,
    }));
  };

  useEffect(() => {
    setShowNeighborhoodOptions(false);
    setShowDemographicOptions(false);
  }, []);

  useEffect(() => {
    setShowDemographicOptions(false);
  }, [neighborhoodOptions, categoryOptions]);

  const onFilter = () => {
    if ((filters?.fromDate && !filters?.toDate) || (!filters?.fromDate && filters?.toDate)) {
      openAlertFilterPopup();
      return;
    }

    const nextFilters = {
      ...(filters ?? {}),
      eventStatus: eventStatus ?? undefined,
    };
    setFilters(nextFilters);
    onApply(nextFilters);
  };

  const quickFilterValues: { id: number; name: string }[] = [
    { id: 1, name: "Last 7 Days" },
    { id: 2, name: "Last 30 Days" },
    { id: 3, name: "Last Quarter" },
  ];

  const eventStatusValues: { id: EventStatus; name: React.ReactNode }[] = [
    {
      id: EventStatus.Active,
      name: (
        <div className="flex items-center gap-2 mb-1 text-gray-700 dark:text-gray-300">
          <Circle
            size={16}
            fill="#00C853"
            stroke="white"
          />
          <span>Active</span>
        </div>
      ),
    },
    {
      id: EventStatus.StartingSoon,
      name: (
        <div className="flex items-center gap-2 mb-1 text-gray-700 dark:text-gray-300">
          <Circle
            size={16}
            fill="#00BCD4"
            stroke="white"
          />
          <span>Starting Soon</span>
        </div>
      ),
    },
    {
      id: EventStatus.EndingSoon,
      name: (
        <div className="flex items-center gap-2 mb-1 text-gray-700 dark:text-gray-300">
          <Circle
            size={16}
            fill="#FF9800"
            stroke="white"
          />
          <span>Ending Soon</span>
        </div>
      ),
    },
  ];

  const attendanceTypeOptions: { key: string; name: string }[] = [
    { key: "all", name: "All Attendance" },
    { key: "user", name: "Users" },
    { key: "guest", name: "Guests" },
  ];

  return (
    <div className="w-full">
      <Card className="mt-5 rounded-2xl space-y-6 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex justify-between items-center px-6 py-4">
          <div className="flex items-center">
            <Filter
              className="mr-2"
              size={16}
              color="#00D6A8"
            />
            <span className="text-lg text-gray-900 dark:text-gray-100">
              Filters & Data Controls
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRefreshAll}
                            disabled={isRefreshing || !hasAnyActiveFilter()}
                            className="flex items-center gap-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                            Refresh
                        </Button> */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="bg-[#00D6A8] text-[#004232] hover:bg-[#00D6A8] px-10 rounded-lg">
                  <Download />
                  <span className="font-bold">Export Data</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-48">
                <DropdownMenuItem
                  onClick={() => {
                    const url = `/heatmap-pdf`;
                    window.open(url, "_blank");
                  }}
                >
                  Export as PDF
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={onExport}
                  disabled={isExporting}
                >
                  {isExporting ? "Exporting..." : "Export as xlsx"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <Row className="px-6 py-3 mt-0 flex flex-row items-end">
          <RegionState
            isLoading={isOptionsLoading}
            error={optionsError}
            onRetry={onRetryOptions}
            skeleton={
              <FilterControlsSkeleton fields={displayMap === LIVE_EVENTS_MAP_KEY ? 5 : 4} />
            }
          >
            <Col className="col-3 w-full">
              <div className="flex">
                <span className="text-gray-700 dark:text-gray-300">Location</span>
              </div>
              <MultiSelect
                value={filters?.neighborhoodIDs || []}
                onChange={(selected) => {
                  setFilters((f) => ({ ...f, neighborhoodIDs: selected }));
                }}
                options={(neighborhoodOptions || [])
                  .map((op) => ({ value: op.key, label: op.value, disabled: false }))
                  .sort((a, b) => a.label.localeCompare(b.label))}
                onClear={() => {
                  setFilters((f) => ({ ...f, neighborhoodIDs: [] }));
                }}
                placeholder={`All ${user?.organization_name || ""}`}
                open={showNeighborhoodOptions}
                onOpenChange={setShowNeighborhoodOptions}
                triggerClassName="min-h-12 rounded-lg leading-7 border-[#B5BABE] dark:border-gray-600 bg-white dark:bg-gray-700"
              />
            </Col>
            <Col className="col-3 w-full">
              {displayMap === USER_DISTRIBUTIONS_MAP_KEY && (
                <>
                  <div className="flex">
                    <span className="text-gray-700 dark:text-gray-300">Demographics</span>
                  </div>
                  <MultiSelect
                    value={filters?.demographicTypeIDs || []}
                    onChange={(selected) => {
                      setFilters((prev) => {
                        const lastSelected = selected[selected.length - 1];

                        let newSelected = selected;

                        if (lastSelected === "0") {
                          newSelected = ["0"];
                        } else {
                          newSelected = selected.filter((v) => v !== "0");
                        }

                        return { ...prev, demographicTypeIDs: newSelected };
                      });
                    }}

                    options={getDemographicTypeOptions() || []}
                    onClear={() => {
                      setFilters((f) => ({ ...f, demographicTypeIDs: [] }));
                    }}
                    placeholder="All Users"
                    open={showDemographicOptions}
                    onOpenChange={setShowDemographicOptions}
                    triggerClassName="min-h-12 rounded-lg leading-7 border-[#B5BABE] dark:border-gray-600 bg-white dark:bg-gray-700"
                  />
                </>
              )}
              {displayMap === LIVE_EVENTS_MAP_KEY && (
                <>
                  <div className="flex">
                    <span className="text-gray-700 dark:text-gray-300">Category</span>
                  </div>
                  <MultiSelect
                    value={filters?.categoryIDs || []}
                    onChange={(selected) => {
                      setFilters((f) => ({ ...f, categoryIDs: selected }));
                    }}
                    options={(categoryOptions || [])
                      .map((op) => ({ value: op.key, label: op.value, disabled: false }))
                      .sort((a, b) => a.label.localeCompare(b.label))}
                    onClear={() => {
                      setFilters((f) => ({ ...f, categoryIDs: [] }));
                    }}
                    placeholder="All Categories"
                    open={showCategoryOptions}
                    onOpenChange={setShowCategoryOptions}
                    triggerClassName="min-h-12 rounded-lg leading-7 border-[#B5BABE] dark:border-gray-600 bg-white dark:bg-gray-700"
                  />
                </>
              )}
            </Col>
            {displayMap === USER_DISTRIBUTIONS_MAP_KEY ? (
              <Col className="col-2 min-w-[300px]">
                <div className="flex">
                  <span className="text-gray-700 dark:text-gray-300">Date Range</span>
                </div>
                <div className="flex items-center">
                  <DatePicker
                    ref={fromDatePickerRef}
                    className="h-12 border rounded-lg border-[#B5BABE] dark:border-gray-600 bg-white dark:bg-gray-700"
                    value={filters?.fromDate ?? undefined}
                    onChange={(date) => {
                      setFilters((f) => ({ ...f, fromDate: date }));
                    }}
                    placeholder={"Start Date"}
                    placeholderIconColor="#00D6A8"
                    dateFormat="MM/dd/yyyy"
                  />
                  <span className="mx-2 text-gray-700 dark:text-gray-300">to</span>
                  <DatePicker
                    ref={toDatePickerRef}
                    className="h-12 border rounded-lg border-[#B5BABE] dark:border-gray-600 bg-white dark:bg-gray-700"
                    value={filters?.toDate ?? undefined}
                    onChange={(date) => {
                      setFilters((f) => ({ ...f, toDate: date }));
                    }}
                    placeholder={"End Date"}
                    placeholderIconColor="#00D6A8"
                    dateFormat="MM/dd/yyyy"
                  />
                </div>
              </Col>
            ) : (
              <>
                <Col className="col-2 w-full">
                  <div className="flex">
                    <span className="text-gray-700 dark:text-gray-300">Event Status</span>
                  </div>
                  <Select
                    value={eventStatus ? eventStatus.toString() : ""}
                    onValueChange={handleChangeEventStatus}
                  >
                    <SelectTrigger className="h-12 border rounded-lg border-[#B5BABE] dark:border-gray-600 bg-white dark:bg-gray-700">
                      <SelectValue placeholder="All Events" />
                    </SelectTrigger>
                    <SelectContent>
                      {eventStatusValues.map((status) => (
                        <SelectItem
                          key={status.id}
                          value={String(status.id)}
                          className="cursor-pointer"
                        >
                          {status.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Col>
                <Col className="col-2 w-full">
                  <div className="flex">
                    <span className="text-gray-700 dark:text-gray-300">Attendance Type</span>
                  </div>
                  <Select
                    value={filters?.attendanceType || ""}
                    onValueChange={(value) =>
                      setFilters((f) => ({
                        ...(f ?? {}),
                        attendanceType: value,
                      }))
                    }
                  >
                    <SelectTrigger className="h-12 border rounded-lg border-[#B5BABE] dark:border-gray-600 bg-white dark:bg-gray-700">
                      <SelectValue placeholder="All Attendance" />
                    </SelectTrigger>
                    <SelectContent>
                      {attendanceTypeOptions.map((option) => (
                        <SelectItem
                          key={option.key}
                          value={String(option.key)}
                          className="cursor-pointer"
                        >
                          {option.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Col>
              </>
            )}

            <Col className="col-2 w-full">
              <div className="flex">
                <span className="text-gray-700 dark:text-gray-300">Quick Filters</span>
              </div>
              <Select
                value={filters?.quickFilterDays ? String(filters.quickFilterDays) : ""}
                onValueChange={handleChangeQuickFilter}
              >
                <SelectTrigger className="h-12 border rounded-lg border-[#B5BABE] dark:border-gray-600 bg-white dark:bg-gray-700">
                  <SelectValue placeholder="Quick Filter" />
                </SelectTrigger>

                <SelectContent>
                  {quickFilterValues.map((city) => (
                    <SelectItem
                      key={city.id}
                      value={String(city.id)}
                      className="cursor-pointer"
                    >
                      {city.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Col>
          </RegionState>
          <Col className="col-2 w-full">
            <Button
              onClick={() => onFilter()}
              className="h-12 bg-[#00D6A8] text-[#004232] hover:bg-[#00D6A8] rounded-lg font-bold"
            >
              Apply Filters
            </Button>
          </Col>
        </Row>
      </Card>
      <ActionModal
        open={alertFilterOpen}
        onClose={closeAlertFilterPopup}
        content={
          <AlertTimeFilterPopup
            onOk={handleAlertOk}
            onClearFilter={handleClearDateFilter}
          />
        }
      />
    </div>
  );
};

export default Filters;
